import { FunctionComponent } from "react";
import tinycolors from "tinycolor2";
import { getEnquiryFieldSettings } from "../../selectors/modules.js";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/enquiry-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  EnquiryModuleSettings,
  EnquiryWidgetSettings,
  LegalNavId,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getFallbackLanguage,
  getSupportedLanguage,
  resolveLink,
} from "../../utils/utils.js";
import EnquiryWidget from "../EnquiryWidget.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

interface Props extends BaseModuleProps<EnquiryModuleSettings> {}

const EnquiryModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    settings: { textAlign, layout },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
  isPreview,
}) => {
  const site = useAppSelector((state) => getActiveSite(state.sites));
  const pages = useAppSelector((state) => state.pages);
  const modules = useAppSelector((state) => state.modules);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const fallbackLanguageId = getFallbackLanguage(site, languageId);

  const privacyURL = resolveLink({
    fallbackLanguageId,
    isPreview,
    languageId,
    pageId: pages.byAlias["privacy" satisfies LegalNavId],
    pages,
  }).href;

  const settings: EnquiryWidgetSettings = {
    fieldSettings: getEnquiryFieldSettings(modules, languageId),
    propertyId: site.accommodation.id,
    lang: getSupportedLanguage(languageId, ["de", "it", "en", "fr"]),
    layout,
    colorScheme: {
      ...scheme,
      main: {
        ...scheme.main,
        separator: tinycolors(scheme.main.separator).lighten(2).toRgbString(),
      },
    },
    privacyURL,
  };

  // Don’t render anything before accommodation is loaded
  if (!settings.propertyId) return null;

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={moduleId}
      className={styles.EnquiryModule}
      colors={{ background: scheme.main.separator, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      {<EnquiryWidget settings={settings} />}
    </ModuleWithHeadings>
  );
};

export default EnquiryModule;
